import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "uploadForm",
    "fileInput",
    "preview",
    "previewContainer",
    "previewList",
    "notes",
    "taggedFigure",
    "taggedFiguresList",
    "taggedCustomFigure",
    "taggedCustomFiguresList",
    "dropZone",
    "imageCounter",
    "progressContainer",
    "taggingSection",
    "captionSection",
    "uploadStatusList",
    "overallProgress",
    "overallProgressBar",
    "uploadButton",
    "scrollableContent"
  ]

  connect() {
    this.taggedFigureIds = new Set()
    this.images = []
    this.currentImageIndex = 0
    this.tagsByImage = new Map()
    this.customTagsByImage = new Map()
    this.captionsByImage = new Map()

    // Add drag and drop event listeners
    this.element.addEventListener('dragover', this.handleDragOver.bind(this))
    this.element.addEventListener('dragleave', this.handleDragLeave.bind(this))
    this.element.addEventListener('drop', this.handleDrop.bind(this))

    // Add caption change listener
    this.notesTarget.addEventListener('input', this.handleCaptionChange.bind(this))
  }

  disconnect() {
    // Remove event listeners when controller is disconnected
    this.element.removeEventListener('dragover', this.handleDragOver.bind(this))
    this.element.removeEventListener('dragleave', this.handleDragLeave.bind(this))
    this.element.removeEventListener('drop', this.handleDrop.bind(this))
    this.notesTarget.removeEventListener('input', this.handleCaptionChange.bind(this))
  }

  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.uploadFormTarget.classList.contains('hidden')) {
      this.dropZoneTarget.classList.add('border-blue-500', 'bg-blue-500/10')
    }
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('border-blue-500', 'bg-blue-500/10')
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('border-blue-500', 'bg-blue-500/10')

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.fileInputTarget.files = files
      this.handleFileSelect({ target: this.fileInputTarget })
    }
  }

  handleFileSelect(event) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    // Filter for image files only
    const imageFiles = files.filter(file => file.type.startsWith('image/'))
    if (imageFiles.length === 0) return

    this.images = imageFiles
    this.currentImageIndex = 0
    this.tagsByImage.clear()
    this.customTagsByImage.clear()
    this.captionsByImage.clear()
    this.updatePreviews()
    this.updateImageCounter()
    this.previewContainerTarget.classList.remove('hidden')
    this.taggingSectionTarget.classList.remove('hidden')
    this.captionSectionTarget.classList.remove('hidden')

    // Auto-tag the figure if we have a stored figure ID
    if (this.autoTagFigureId) {
      this.taggedFigureTarget.value = this.autoTagFigureId
      this.addFigureTag()
      this.autoTagFigureId = null
    }

    // Scroll to the bottom of the modal content
    this.scrollToBottom()
  }

  scrollToBottom() {
    // Get the scrollable content container
    const scrollableContent = this.element.querySelector('.flex-1.overflow-y-auto')
    if (scrollableContent) {
      // Use a longer timeout to ensure content is rendered
      setTimeout(() => {
        // Scroll to the maximum height
        scrollableContent.scrollTop = scrollableContent.scrollHeight
      }, 300)
    }
  }

  updatePreviews() {
    // Clear existing previews
    this.previewListTarget.innerHTML = ''
    
    // Create thumbnails for all images
    const thumbnails = this.images.map((file, index) => {
      const thumbnail = document.createElement('div')
      thumbnail.setAttribute('data-thumbnail', '')
      thumbnail.dataset.index = index
      thumbnail.className = `flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden cursor-pointer relative ${index === this.currentImageIndex ? 'ring-2 ring-blue-500' : ''}`
      thumbnail.innerHTML = `
        <div class="w-full h-full bg-gray-800 rounded-lg flex items-center justify-center">
          <i class="fas fa-spinner fa-spin text-gray-600"></i>
        </div>
        <div class="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
          <i class="fas fa-user"></i>
          <span>${this.tagsByImage.get(index)?.size || 0}</span>
        </div>
      `
      thumbnail.addEventListener('click', () => this.showImage(index))
      this.previewListTarget.appendChild(thumbnail)
      return thumbnail
    })
    
    // Load images in parallel but maintain order
    this.images.forEach((file, index) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const thumbnail = thumbnails[index]
        thumbnail.innerHTML = `
          <img src="${e.target.result}" class="w-full h-full object-cover rounded-lg" alt="Thumbnail">
          <div class="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <i class="fas fa-user"></i>
            <span>${this.tagsByImage.get(index)?.size || 0}</span>
          </div>
        `
      }
      reader.readAsDataURL(file)
    })

    // Show current image
    this.currentImageIndex = 0
    this.showCurrentImage()
  }

  showCurrentImage() {
    if (this.images.length === 0) return

    const reader = new FileReader()
    reader.onload = (e) => {
      this.previewTarget.src = e.target.result
    }
    reader.readAsDataURL(this.images[this.currentImageIndex])

    // Update tags for current image
    this.updateTagsForCurrentImage()
    this.updateCustomTagsForCurrentImage()

    // Update caption for current image
    this.notesTarget.value = this.captionsByImage.get(this.currentImageIndex) || ''
  }

  showImage(index) {
    this.currentImageIndex = index
    this.showCurrentImage()
    
    // Update selected state of thumbnails without recreating them
    const thumbnails = this.previewListTarget.querySelectorAll('[data-thumbnail]')
    thumbnails.forEach(thumb => {
      const thumbIndex = parseInt(thumb.dataset.index)
      if (thumbIndex === index) {
        thumb.classList.add('ring-2', 'ring-blue-500')
      } else {
        thumb.classList.remove('ring-2', 'ring-blue-500')
      }
    })
  }

  previousImage() {
    if (this.currentImageIndex > 0) {
      this.showImage(this.currentImageIndex - 1)
    }
  }

  nextImage() {
    if (this.currentImageIndex < this.images.length - 1) {
      this.showImage(this.currentImageIndex + 1)
    }
  }

  removeCurrentImage() {
    if (this.images.length === 0) return

    this.images.splice(this.currentImageIndex, 1)
    this.tagsByImage.delete(this.currentImageIndex)
    this.customTagsByImage.delete(this.currentImageIndex)
    this.captionsByImage.delete(this.currentImageIndex)

    if (this.currentImageIndex >= this.images.length) {
      this.currentImageIndex = Math.max(0, this.images.length - 1)
    }

    if (this.images.length === 0) {
      this.previewContainerTarget.classList.add('hidden')
      this.taggingSectionTarget.classList.add('hidden')
      this.captionSectionTarget.classList.add('hidden')
    } else {
      this.updatePreviews()
      this.updateImageCounter()
    }
  }

  updateImageCounter() {
    if (this.images.length === 0) {
      this.imageCounterTarget.textContent = 'No photos selected'
    } else {
      this.imageCounterTarget.textContent = `${this.currentImageIndex + 1} of ${this.images.length} photos`
    }
  }

  addFigureTag() {
    const figureId = this.taggedFigureTarget.value
    if (!figureId) return

    const currentTags = this.tagsByImage.get(this.currentImageIndex) || new Set()
    currentTags.add(figureId)
    this.tagsByImage.set(this.currentImageIndex, currentTags)

    const figureName = this.taggedFigureTarget.options[this.taggedFigureTarget.selectedIndex].text
    const tagElement = document.createElement('div')
    tagElement.className = 'flex items-center justify-between bg-gray-800 p-2 rounded-md border border-blue-500'
    tagElement.innerHTML = `
      <span class="text-white">${figureName}</span>
      <button type="button" 
              class="text-gray-400 hover:text-white"
              data-action="click->collection-photos#removeFigureTag"
              data-figure-id="${figureId}">
        <i class="fas fa-times"></i>
      </button>
    `

    this.taggedFiguresListTarget.appendChild(tagElement)
    this.taggedFigureTarget.options[this.taggedFigureTarget.selectedIndex].disabled = true
    this.taggedFigureTarget.value = ''
    // this.updatePreviews()
  }

  removeFigureTag(event) {
    const figureId = event.currentTarget.dataset.figureId
    const currentTags = this.tagsByImage.get(this.currentImageIndex)
    if (currentTags) {
      currentTags.delete(figureId)
      this.tagsByImage.set(this.currentImageIndex, currentTags)
    }

    const options = Array.from(this.taggedFigureTarget.options)
    const option = options.find(opt => opt.value === figureId)
    if (option) {
      option.disabled = false
    }

    event.currentTarget.closest('div').remove()
    this.updatePreviews()
  }

  applyTagsToAll() {
    const currentTags = this.tagsByImage.get(this.currentImageIndex)
    const currentCustomTags = this.customTagsByImage.get(this.currentImageIndex)

    this.images.forEach((_, index) => {
      if (index === this.currentImageIndex) return
      if (currentTags && currentTags.size > 0) {
        this.tagsByImage.set(index, new Set(currentTags))
      }
      if (currentCustomTags && currentCustomTags.size > 0) {
        this.customTagsByImage.set(index, new Set(currentCustomTags))
      }
    })

    this.updatePreviews()
  }

  addCustomFigureTag() {
    if (!this.hasTaggedCustomFigureTarget) return
    const customFigureId = this.taggedCustomFigureTarget.value
    if (!customFigureId) return

    const currentTags = this.customTagsByImage.get(this.currentImageIndex) || new Set()
    currentTags.add(customFigureId)
    this.customTagsByImage.set(this.currentImageIndex, currentTags)

    const customFigureName = this.taggedCustomFigureTarget.options[this.taggedCustomFigureTarget.selectedIndex].text
    const tagElement = document.createElement('div')
    tagElement.className = 'flex items-center justify-between bg-gray-800 p-2 rounded-md border border-purple-500'
    tagElement.innerHTML = `
      <span class="text-white"><i class="fas fa-cubes mr-2 text-purple-400"></i>${customFigureName}</span>
      <button type="button"
              class="text-gray-400 hover:text-white"
              data-action="click->collection-photos#removeCustomFigureTag"
              data-custom-figure-id="${customFigureId}">
        <i class="fas fa-times"></i>
      </button>
    `

    this.taggedCustomFiguresListTarget.appendChild(tagElement)
    this.taggedCustomFigureTarget.options[this.taggedCustomFigureTarget.selectedIndex].disabled = true
    this.taggedCustomFigureTarget.value = ''
  }

  removeCustomFigureTag(event) {
    const customFigureId = event.currentTarget.dataset.customFigureId
    const currentTags = this.customTagsByImage.get(this.currentImageIndex)
    if (currentTags) {
      currentTags.delete(customFigureId)
      this.customTagsByImage.set(this.currentImageIndex, currentTags)
    }

    if (this.hasTaggedCustomFigureTarget) {
      const option = Array.from(this.taggedCustomFigureTarget.options).find(opt => opt.value === customFigureId)
      if (option) option.disabled = false
    }

    event.currentTarget.closest('div').remove()
  }

  updateCustomTagsForCurrentImage() {
    if (!this.hasTaggedCustomFiguresListTarget || !this.hasTaggedCustomFigureTarget) return
    this.taggedCustomFiguresListTarget.innerHTML = ''
    Array.from(this.taggedCustomFigureTarget.options).forEach(option => {
      option.disabled = false
    })

    const currentTags = this.customTagsByImage.get(this.currentImageIndex)
    if (currentTags) {
      currentTags.forEach(customFigureId => {
        const option = Array.from(this.taggedCustomFigureTarget.options).find(opt => opt.value === customFigureId)
        if (option) {
          option.disabled = true
          const tagElement = document.createElement('div')
          tagElement.className = 'flex items-center justify-between bg-gray-800 p-2 rounded-md border border-purple-500'
          tagElement.innerHTML = `
            <span class="text-white"><i class="fas fa-cubes mr-2 text-purple-400"></i>${option.text}</span>
            <button type="button"
                    class="text-gray-400 hover:text-white"
                    data-action="click->collection-photos#removeCustomFigureTag"
                    data-custom-figure-id="${customFigureId}">
              <i class="fas fa-times"></i>
            </button>
          `
          this.taggedCustomFiguresListTarget.appendChild(tagElement)
        }
      })
    }
  }

  updateTagsForCurrentImage() {
    this.taggedFiguresListTarget.innerHTML = ''
    Array.from(this.taggedFigureTarget.options).forEach(option => {
      option.disabled = false
    })

    const currentTags = this.tagsByImage.get(this.currentImageIndex)
    if (currentTags) {
      currentTags.forEach(figureId => {
        const option = Array.from(this.taggedFigureTarget.options).find(opt => opt.value === figureId)
        if (option) {
          option.disabled = true
          const tagElement = document.createElement('div')
          tagElement.className = 'flex items-center justify-between bg-gray-800 p-2 rounded-md border border-blue-500'
          tagElement.innerHTML = `
            <span class="text-white">${option.text}</span>
            <button type="button" 
                    class="text-gray-400 hover:text-white"
                    data-action="click->collection-photos#removeFigureTag"
                    data-figure-id="${figureId}">
              <i class="fas fa-times"></i>
            </button>
          `
          this.taggedFiguresListTarget.appendChild(tagElement)
        }
      })
    }
  }

  async uploadPhotos(event) {
    event.preventDefault()
    
    if (this.images.length === 0) return

    // Create progress container if it doesn't exist
    let progressContainer = document.querySelector('[data-collection-photos-target="progressContainer"]')
    if (!progressContainer) {
      progressContainer = document.createElement('div')
      progressContainer.setAttribute('data-collection-photos-target', 'progressContainer')
      progressContainer.className = 'mt-4 space-y-2'
      this.previewContainerTarget.appendChild(progressContainer)
    }

    // Create combined progress bar
    progressContainer.innerHTML = `
      <div class="mb-4">
        <div class="flex justify-between items-center mb-2">
          <span class="text-sm text-gray-400">Overall Progress</span>
          <span class="text-sm text-gray-400" data-collection-photos-target="overallProgress">0%</span>
        </div>
        <div class="h-2 bg-gray-700 rounded-full overflow-hidden">
          <div class="h-full bg-blue-500 rounded-full transition-all duration-300" style="width: 0%" data-collection-photos-target="overallProgressBar"></div>
        </div>
      </div>
      <div class="flex flex-wrap gap-2 max-h-24 overflow-y-auto" data-collection-photos-target="uploadStatusList">
        <!-- Individual upload statuses will be added here -->
      </div>
    `

    // Disable upload button
    const uploadButton = event.currentTarget
    uploadButton.disabled = true
    uploadButton.classList.add('opacity-50', 'cursor-not-allowed')

    let successCount = 0
    let errorCount = 0
    let totalProgress = 0
    let lastTurboStreamResponse = null

    try {
      // Upload each image sequentially
      for (let i = 0; i < this.images.length; i++) {
        const image = this.images[i]
        const tags = this.tagsByImage.get(i)
        const caption = this.captionsByImage.get(i) || ''

        // Create status element for this image
        const statusElement = document.createElement('div')
        statusElement.className = 'flex items-center gap-1 text-sm bg-gray-800 px-2 py-1 rounded'
        statusElement.innerHTML = `
          <div class="w-4 h-4 flex items-center justify-center">
            <i class="fas fa-spinner fa-spin text-gray-400"></i>
          </div>
          <span class="text-gray-400">${i + 1}</span>
        `
        this.uploadStatusListTarget.appendChild(statusElement)

        const customTags = this.customTagsByImage.get(i)

        const formData = new FormData()
        formData.append('image', image)
        formData.append('notes', caption)

        if (tags) {
          Array.from(tags).forEach(figureId => {
            formData.append('tagged_figure_ids[]', figureId)
          })
        }

        if (customTags) {
          Array.from(customTags).forEach(customFigureId => {
            formData.append('tagged_custom_figure_ids[]', customFigureId)
          })
        }

        try {
          const response = await fetch(`/my-collections/${this.element.dataset.collectionSlug}/upload_photo`, {
            method: 'POST',
            body: formData,
            headers: {
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
              'Accept': 'text/vnd.turbo-stream.html'
            }
          })

          if (response.ok) {
            successCount++
            statusElement.innerHTML = `
              <div class="w-6 h-6 flex items-center justify-center text-green-500">
                <i class="fas fa-check"></i>
              </div>
              <span class="text-gray-400">Image ${i + 1}</span>
            `
            // Store the Turbo Stream response
            lastTurboStreamResponse = await response.text()
          } else {
            errorCount++
            const data = await response.json()
            statusElement.innerHTML = `
              <div class="w-6 h-6 flex items-center justify-center text-red-500">
                <i class="fas fa-times"></i>
              </div>
              <span class="text-gray-400">Image ${i + 1} failed: ${data.error || 'Unknown error'}</span>
            `
          }
        } catch (error) {
          errorCount++
          statusElement.innerHTML = `
            <div class="w-6 h-6 flex items-center justify-center text-red-500">
              <i class="fas fa-times"></i>
            </div>
            <span class="text-gray-400">Image ${i + 1} failed: ${error.message}</span>
          `
        }

        // Update overall progress
        totalProgress = ((i + 1) / this.images.length) * 100
        this.overallProgressTarget.textContent = `${Math.round(totalProgress)}%`
        this.overallProgressBarTarget.style.width = `${totalProgress}%`
      }

      // Show final status
      const statusMessage = document.createElement('div')
      statusMessage.className = 'mt-4 text-center'
      if (errorCount === 0) {
        statusMessage.innerHTML = `
          <div class="text-green-500">
            <i class="fas fa-check-circle mr-2"></i>
            All ${successCount} images uploaded successfully
          </div>
        `
      } else if (successCount === 0) {
        statusMessage.innerHTML = `
          <div class="text-red-500">
            <i class="fas fa-exclamation-circle mr-2"></i>
            Failed to upload all ${errorCount} images
          </div>
        `
      } else {
        statusMessage.innerHTML = `
          <div class="text-yellow-500">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            Uploaded ${successCount} images, failed to upload ${errorCount} images
          </div>
        `
      }
      progressContainer.appendChild(statusMessage)

      // Close modal after a delay if all uploads were successful
      if (errorCount === 0) {
        setTimeout(() => {
          this.closeUploadModal()
          // Process the last Turbo Stream response
          if (lastTurboStreamResponse) {
            Turbo.renderStreamMessage(lastTurboStreamResponse)
          }
        }, 2000)
      }
    } catch (error) {
      console.error('Error during upload process:', error)
      alert('Error during upload process')
    } finally {
      // Re-enable upload button
      uploadButton.disabled = false
      uploadButton.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  resetForm() {
    this.fileInputTarget.value = ''
    this.previewTarget.src = ''
    this.previewContainerTarget.classList.add('hidden')
    this.taggingSectionTarget.classList.add('hidden')
    this.captionSectionTarget.classList.add('hidden')
    this.notesTarget.value = ''
    this.taggedFigureTarget.value = ''
    this.taggedFigureIds.clear()
    this.taggedFiguresListTarget.innerHTML = ''
    if (this.hasTaggedCustomFigureTarget) {
      this.taggedCustomFigureTarget.value = ''
      Array.from(this.taggedCustomFigureTarget.options).forEach(o => (o.disabled = false))
    }
    if (this.hasTaggedCustomFiguresListTarget) {
      this.taggedCustomFiguresListTarget.innerHTML = ''
    }
    this.images = []
    this.currentImageIndex = 0
    this.tagsByImage.clear()
    this.customTagsByImage.clear()
    this.captionsByImage.clear()
    this.updateImageCounter()
  }

  async deleteImage(event) {
    const imageId = event.currentTarget.dataset.figureUserImageId
    if (!confirm('Are you sure you want to delete this image?')) return

    try {
      const response = await fetch(`/my-collections/${this.element.dataset.collectionSlug}/delete_image?image_id=${imageId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        alert('Failed to delete image')
      }
    } catch (error) {
      console.error('Error deleting image:', error)
      alert('Error deleting image')
    }
  }

  openUploadModal(event) {
    this.uploadFormTarget.classList.remove('hidden')
    this.resetForm()
    
    // Get the figure ID from the button that triggered the modal
    const button = event.currentTarget
    this.autoTagFigureId = button.dataset.collectionPhotosFigureId
  }

  closeUploadModal() {
    this.uploadFormTarget.classList.add('hidden')
    this.resetForm()
  }

  handleCaptionChange(event) {
    this.captionsByImage.set(this.currentImageIndex, event.target.value)
  }
} 